"use client";

/**
 * A CONFIRMAÇÃO DO PORTAL — o substituto de `window.confirm` (09/10/2026,
 * pedido do dono: "não quero nenhum tipo de alert nativo do javascript, quero
 * tudo em modal no centro da tela"). Nenhuma tela usa `alert`, `confirm` ou
 * `prompt` do navegador: quem precisa perguntar chama `await confirmar({...})`.
 *
 * Por que uma FUNÇÃO e não um hook: as perguntas moram em `useMemo` de ações,
 * em ouvinte de documento (o clique num link com rascunho) e em funções fora
 * de componente (`confirmarGasto`). Um store de módulo deixa todas chamarem do
 * mesmo jeito; o `<Confirmacoes />` do layout raiz desenha uma pergunta por vez.
 *
 * O FOCO CAI EM "Cancelar" (o mesmo de `processamento/modais.tsx`): um Enter
 * distraído não confirma nada. Escape, clique no fundo e "Fechar" respondem não.
 */

import { useEffect, useRef, useSyncExternalStore, type ReactElement, type ReactNode } from "react";

import { Modal } from "@/components/modal";

export type PedidoDeConfirmacao = {
  titulo: string;
  /** Texto (parágrafos separados por linha em branco) ou JSX já montado. */
  mensagem: ReactNode;
  /** O rótulo do botão que confirma: o verbo da ação ("Drenar", "Salvar"). */
  confirmar?: string;
  cancelar?: string;
  /** `perigo` = a ação desfaz ou interrompe algo: o botão sai vermelho. */
  tom?: "normal" | "perigo";
};

type Aberta = PedidoDeConfirmacao & {
  id: number;
  origem: HTMLElement | null;
  responder: (ok: boolean) => void;
};

let fila: readonly Aberta[] = [];
let ultimoId = 0;
const ouvintes = new Set<() => void>();

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

function trocarFila(nova: readonly Aberta[]): void {
  fila = nova;
  for (const ouvinte of ouvintes) ouvinte();
}

/** `true` = a pessoa leu e mandou seguir. Perguntas simultâneas entram em fila. */
export function confirmar(pedido: PedidoDeConfirmacao): Promise<boolean> {
  const ativo = document.activeElement;
  const origem = ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
  return new Promise((resolver) => {
    const id = ++ultimoId;
    const responder = (ok: boolean) => {
      // Só a primeira resposta vale: o "OK" desmonta o modal, e nada depois dele responde de novo.
      if (!fila.some((a) => a.id === id)) return;
      trocarFila(fila.filter((a) => a.id !== id));
      resolver(ok);
    };
    trocarFila([...fila, { ...pedido, id, origem, responder }]);
  });
}

/** Caixa de destaque neutra, para o número que a pessoa tem de ler. */
export function Destaque({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="rounded-lg border border-app-border bg-black/[0.02] px-3 py-2 dark:bg-white/[0.03]">{children}</div>
  );
}

/** Caixa de aviso (âmbar): gasto, efeito no processamento. */
export function Aviso({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
      {children}
    </div>
  );
}

const BOTAO_CANCELAR =
  "rounded-md border border-app-border px-3 py-1.5 text-xs font-medium transition hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-white/10";
const BOTAO_DO_TOM = {
  normal:
    "rounded-md bg-brand-700 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-brand-300 dark:text-brand-900 dark:hover:bg-brand-100",
  perigo:
    "rounded-md bg-red-700 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:bg-red-400 dark:text-red-950 dark:hover:bg-red-300",
} as const;

function Texto({ texto }: { texto: string }): ReactElement {
  return (
    <>
      {texto.split(/\n{2,}/).map((paragrafo, i) => (
        <p key={i} className="whitespace-pre-line">
          {paragrafo}
        </p>
      ))}
    </>
  );
}

function ModalDeConfirmacao({ aberta }: { aberta: Aberta }): ReactElement {
  const cancelar = useRef<HTMLButtonElement>(null);
  // Roda depois do `showModal()` do `Modal` (o efeito do filho roda antes do do pai).
  useEffect(() => {
    cancelar.current?.focus();
  }, []);
  const nao = () => aberta.responder(false);
  return (
    <Modal
      tamanho="confirmacao"
      titulo={aberta.titulo}
      aoFechar={nao}
      devolverFocoA={aberta.origem}
      acoes={
        <>
          <button ref={cancelar} type="button" onClick={nao} className={BOTAO_CANCELAR}>
            {aberta.cancelar ?? "Cancelar"}
          </button>
          <button
            type="button"
            onClick={() => aberta.responder(true)}
            className={BOTAO_DO_TOM[aberta.tom ?? "normal"]}
          >
            {aberta.confirmar ?? "Confirmar"}
          </button>
        </>
      }
    >
      <div className="space-y-3 px-4 py-4 text-sm leading-relaxed">
        {typeof aberta.mensagem === "string" ? <Texto texto={aberta.mensagem} /> : aberta.mensagem}
      </div>
    </Modal>
  );
}

/** Montado uma vez, no layout raiz: desenha a pergunta da vez. */
export function Confirmacoes(): ReactElement | null {
  const aberta = useSyncExternalStore(
    assinar,
    () => fila[0] ?? null,
    () => null,
  );
  return aberta === null ? null : <ModalDeConfirmacao key={aberta.id} aberta={aberta} />;
}
